import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { isDemo } from "@/lib/demo";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import {
  Globe, Loader2, Link2, Unlink, CheckCircle2, XCircle, MapPin, Phone, Star, ExternalLink,
  ShieldCheck, KeyRound, Image as ImageIcon, RefreshCw, Search,
} from "lucide-react";

const Overline = ({ children }) => <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">{children}</div>;
const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-white rounded-xl border border-brand-gold/25 shadow-[0_1px_2px_rgba(15,58,45,0.04)] ${className}`} {...p}>{children}</div>
);
const Head = ({ icon: Icon, title, sub }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <Icon size={20} className="text-brand-gold" strokeWidth={1.9} />
    <div><h3 className="font-serif text-[20px] text-brand-ink leading-none">{title}</h3>{sub && <p className="text-[12.5px] text-brand-muted mt-1">{sub}</p>}</div>
  </div>
);
const Pill = ({ ok, on, off }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-brand-cream text-brand-muted border-brand-gold/30"}`}>
    {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {ok ? on : off}
  </span>
);

function GoogleSearchResults({ scope, version, site }) {
  const [gs, setGs] = useState(null);
  useEffect(() => { api.external(scope.tenant, scope.hotel).then((d) => setGs(d.profiles?.google_search || null)).catch(() => setGs(null)); }, [scope.tenant, scope.hotel, version]);
  if (!gs?.results?.length) return null;
  const host = (site || "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  const own = host ? gs.results.filter((r) => (r.url || "").includes(host)) : [];
  return (
    <Card className="p-6 mb-6" data-testid="google-search-results">
      <Head icon={Search} title={`Come appare su Google: primi ${gs.results.length} risultati`} sub={`Ricerca reale «${gs.query}» · chi parla dell'hotel e in che ordine (${gs.via === "serper" ? "ricerca Google dedicata, senza credito del fornitore dati" : "letto dal fornitore dati"}).`} />
      <div className="mt-3 text-[12.5px] text-brand-muted">{own.length ? `${own.length} risultati portano al sito ufficiale (prima posizione: ${own[0].position}); ` : host ? "il sito ufficiale non compare tra questi risultati; " : ""}{gs.results.filter((r) => /booking|expedia|tripadvisor|trip\.com|hotels\.com/i.test(r.url)).length} a portali di prenotazione.</div>
      <ol className="mt-3 divide-y divide-brand-gold/15">
        {gs.results.map((r) => (
          <li key={r.position} className="py-2.5 flex gap-3" data-testid={`google-result-${r.position}`}>
            <span className="w-6 h-6 rounded-full bg-brand-green text-brand-cream text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{r.position}</span>
            <div className="min-w-0">
              <a href={r.url} target="_blank" rel="noreferrer" className="text-[13.5px] font-semibold text-brand-ink hover:text-brand-gold leading-snug block truncate">{r.title || r.url}</a>
              <div className="text-[11px] text-brand-muted truncate">{r.displayed_url || r.url}</div>
              {r.description && <p className="text-[12px] text-brand-ink/80 mt-0.5 leading-snug">{r.description}</p>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function GbpGuide() {
  const cb = `${window.location.origin}/api/google/oauth/callback`;
  return (
    <div className="mt-4 rounded-lg border border-brand-gold/25 bg-brand-cream/40 p-4 text-[12.5px] text-brand-ink space-y-2" data-testid="gbp-guide">
      <div className="font-semibold">Cosa serve per collegare il Business Profile</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>L'account Google con cui ti colleghi deve essere <strong>proprietario o gestore</strong> della scheda dell'hotel su business.google.com.</li>
        <li>Nel progetto Google Cloud di LEUTELIA devono essere abilitate le <strong>Business Profile API</strong> (Account Management, Business Information, My Business v4) con accesso approvato da Google: senza approvazione la quota è zero e Google risponde «429».</li>
        <li>L'indirizzo di ritorno <code className="bg-white px-1 rounded border border-brand-gold/30 break-all">{cb}</code> deve comparire tra gli «URI di reindirizzamento autorizzati» del client OAuth.</li>
      </ol>
      <p className="text-brand-muted">Una volta collegato: scelta della sede, stato di completezza della scheda, recensioni con testo completo e risposta direttamente da LEUTELIA.</p>
    </div>
  );
}

export default function Google({ ccu, scope, reload }) {
  const [status, setStatus] = useState(null);
  const [place, setPlace] = useState(null);
  const [health, setHealth] = useState(null);
  const [locations, setLocations] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const st = await api.googleStatus(scope.tenant, scope.hotel);
    setStatus(st);
    if (st.place_linked) api.googlePlace(scope.tenant, scope.hotel).then(setPlace).catch(() => setPlace(null));
    if (st.gbp_connected && st.gbp_selection) api.gbpHealth(scope.tenant, scope.hotel).then(setHealth).catch((e) => toast.error(`Salute scheda: ${e.message}`));
    if (st.gbp_connected && !st.gbp_selection) api.gbpLocations(scope.tenant, scope.hotel).then((d) => setLocations(d.locations)).catch((e) => toast.error(e.message));
  }, [scope.tenant, scope.hotel]);
  useEffect(() => { load().catch((e) => toast.error(e.message)); }, [load, ccu.understanding_version]);

  const link = async () => {
    setBusy("link");
    try {
      const out = await api.googlePlaceLink({ tenant: scope.tenant, hotel: scope.hotel });
      toast.success(`Scheda collegata: ${out.place.name}`);
      await reload(); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const select = async (l) => {
    setBusy("select");
    try {
      await api.gbpSelect({ tenant: scope.tenant, hotel: scope.hotel, account_id: l.account_id, location_id: l.location_id });
      toast.success(`Sede selezionata: ${l.title}`); setLocations(null); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const disconnect = async () => {
    setBusy("disc");
    try { await api.googleDisconnect({ tenant: scope.tenant, hotel: scope.hotel }); setHealth(null); toast.success("Account Google scollegato"); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  if (!status) return <div className="flex items-center gap-3 justify-center h-[60vh] text-brand-muted"><Loader2 className="animate-spin text-brand-gold" /><span className="font-mono text-[13px]">google…</span></div>;
  const ci = ccu.canonical_identity || {};

  return (
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in" data-testid="google-module">
      <GoogleSearchResults scope={scope} version={ccu.understanding_version} site={ci.official_site} />
      <Overline>Fonti esterne · Google</Overline>
      <div className="flex items-start gap-4 mt-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center shrink-0"><Globe size={22} className="text-brand-gold" strokeWidth={1.75} /></div>
        <div>
          <h2 className="font-serif text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] text-brand-ink">La tua presenza su Google.</h2>
          <p className="text-[14.5px] text-brand-muted mt-2 max-w-2xl">Scheda pubblica (Places API) e Business Profile del direttore (OAuth). I dati verificati entrano nella comprensione dell'hotel e alimentano Panoramica e moduli.</p>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="google-status-pills">
            <Pill ok={status.places_configured} on="Places API configurata" off="Places API non configurata" />
            <Pill ok={status.place_linked} on="Scheda Google collegata" off="Scheda non collegata" />
            <Pill ok={status.gbp_configured} on="OAuth Business Profile configurato" off="OAuth non configurato" />
            <Pill ok={status.gbp_connected} on="Account direttore collegato" off="Account direttore non collegato" />
          </div>
        </div>
      </div>

      {(!status.places_configured || !status.gbp_configured) && (
        <Card className="p-5 mb-6 border-brand-orange/40 flex items-start gap-3" data-testid="google-env-notice">
          <KeyRound size={18} className="text-brand-orange mt-0.5 shrink-0" />
          <div className="text-[13px] text-brand-ink leading-relaxed">
            <strong>Credenziali Google via variabili d'ambiente sicure</strong> (mai nel repository):
            <code className="ml-1 font-mono text-[12px] text-brand-green">GOOGLE_PLACES_API_KEY</code>, <code className="font-mono text-[12px] text-brand-green">GOOGLE_CLIENT_ID</code>, <code className="font-mono text-[12px] text-brand-green">GOOGLE_CLIENT_SECRET</code>, <code className="font-mono text-[12px] text-brand-green">GOOGLE_REDIRECT_URI</code>.
            Finché mancano, LEUTELIA usa dati <strong>MOCKED</strong> per la scheda Google e le recensioni.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-5 mb-8">
        {/* Scheda pubblica */}
        <Card className="p-6" data-testid="google-place-card">
          <Head icon={MapPin} title="Scheda pubblica (Places)" sub="Dati pubblici della scheda Google: rating, recensioni, foto e indirizzo verificato." />
          {status.place_linked ? (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-5">
              <div className="space-y-3">
                <div className="flex items-baseline gap-3">
                  <div className="font-serif text-[40px] text-brand-ink leading-none" data-testid="google-rating">{ci.google_rating ?? "—"}</div>
                  <div className="text-[12.5px] text-brand-muted">★ su 5 · {ci.google_reviews_count ?? 0} recensioni Google</div>
                </div>
                <Row icon={MapPin} text={ci.address} />
                <Row icon={Phone} text={ci.phone} />
                <Row icon={Globe} text={ci.official_site} link />
                {ci.google_maps_uri && <a href={ci.google_maps_uri} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-green hover:underline" data-testid="google-maps-link"><ExternalLink size={13} /> Apri su Google Maps</a>}
                {place?.opening_hours?.length > 0 && <div className="text-[12px] text-brand-muted">{place.opening_hours[0]}</div>}
                {place?.summary && <p className="text-[13px] text-brand-ink italic">{place.summary}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2 content-start">
                {(ci.images || []).slice(0, 4).map((src, i) => <img key={i} src={src} alt="" className="w-full h-20 object-cover rounded-lg border border-brand-gold/20" />)}
                {!(ci.images || []).length && <div className="col-span-2 flex items-center gap-2 text-[12px] text-brand-muted"><ImageIcon size={14} /> Nessuna foto</div>}
              </div>
            </div>
          ) : (
            <div className="text-[13.5px] text-brand-muted">
              <p>Questo hotel non ha ancora una scheda Google collegata. LEUTELIA cerca <strong>{ci.name}</strong> a <strong>{ci.locality}</strong>, acquisisce rating, recensioni, foto e indirizzo verificato, e aggiorna la comprensione.</p>
              <Button data-testid="google-link-btn" disabled={!status.places_configured || busy === "link"} onClick={link}
                className="mt-4 h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold disabled:opacity-50">
                {busy === "link" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Link2 size={15} className="mr-2" />} Collega la scheda Google
              </Button>
              {!status.places_configured && <div className="text-[11.5px] text-brand-orange mt-2">Richiede GOOGLE_PLACES_API_KEY.</div>}
            </div>
          )}
        </Card>

        {/* Business Profile */}
        {!isDemo() && <Card className="p-6" data-testid="gbp-card">
          <Head icon={ShieldCheck} title="Business Profile del direttore" sub="Collega il tuo account Google per gestire la scheda e rispondere alle recensioni direttamente da LEUTELIA." />
          {!status.gbp_connected ? (
            <div>
              <Button data-testid="gbp-connect-btn" disabled={!status.gbp_configured}
                onClick={async () => { try { window.location.href = (await api.googleOAuthStart(scope.tenant, scope.hotel)).url; } catch (e) { toast.error(e.message); } }}
                className="h-10 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[13px] font-semibold disabled:opacity-50">
                <ShieldCheck size={15} className="mr-2 inline" /> Collega account Google
              </Button>
              <p className="text-[11.5px] text-brand-muted mt-3">Autorizzazione «gestione attività» (business.manage). La chiave di accesso è cifrata e resta solo sul server.</p>
              <GbpGuide />
            </div>
          ) : (
            <div className="space-y-4">
              {locations && (
                <div data-testid="gbp-locations">
                  <div className="text-[12px] font-semibold text-brand-ink mb-2">Quale sede corrisponde a {ci.name}?</div>
                  <div className="space-y-2">
                    {locations.map((l) => (
                      <button key={l.location_id} data-testid={`gbp-location-${l.location_id}`} onClick={() => select(l)} disabled={busy === "select"}
                        className="w-full text-left rounded-lg border border-brand-gold/30 px-4 py-3 hover:bg-brand-cream transition-colors">
                        <div className="text-[13.5px] font-semibold text-brand-ink">{l.title}</div>
                        <div className="text-[12px] text-brand-muted">{l.address} · {l.account_name}</div>
                      </button>
                    ))}
                    {!locations.length && <div className="text-[13px] text-brand-muted italic">Nessuna sede trovata sull'account.</div>}
                  </div>
                  <p className="text-[11.5px] text-brand-muted mt-3" data-testid="gbp-locations-hint">Queste sono le schede gestite dall'account Google appena collegato. Se la scheda di {ci.name} non c'è, il collegamento va fatto con l'account Google che la amministra (di solito il direttore): premi «Scollega» e ripeti dal suo accesso, oppure fatti aggiungere come gestore della scheda.</p>
                </div>
              )}
              {health && (
                <div className="flex gap-5 items-center" data-testid="gbp-health">
                  <div className="relative w-[120px] h-[120px] shrink-0">
                    <ResponsiveContainer width={120} height={120}>
                      <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ value: health.score, fill: health.score >= 80 ? "#16493A" : "#C6902A" }]} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#EDE7D8" }} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center"><div className="font-serif text-[26px] text-brand-ink leading-none">{health.score}%</div><div className="text-[10px] text-brand-muted">completezza</div></div>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {health.checks.map((c) => (
                      <div key={c.key} className="flex items-center gap-2 text-[13px]">
                        {c.ok ? <CheckCircle2 size={15} className="text-emerald-700" /> : <XCircle size={15} className="text-brand-orange" />}
                        <span className={c.ok ? "text-brand-ink" : "text-brand-ink font-semibold"}>{c.label}</span>
                        {c.value != null && <span className="text-[11px] font-mono text-brand-muted">({c.value})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={load} className="h-9 rounded-lg border-brand-green/30 text-brand-green text-[12.5px]"><RefreshCw size={14} className="mr-2" /> Aggiorna</Button>
                <Button data-testid="gbp-disconnect-btn" variant="outline" disabled={busy === "disc"} onClick={disconnect} className="h-9 rounded-lg border-brand-maroon/30 text-brand-maroon text-[12.5px]"><Unlink size={14} className="mr-2" /> Scollega</Button>
              </div>
            </div>
          )}
        </Card>}
      </div>

      {status.place_linked && place?.reviews?.length > 0 && (
        <Card className="p-6" data-testid="google-recent-reviews">
          <Head icon={Star} title="Recensioni Google recenti" sub="Le 5 più rilevanti dalla scheda pubblica. Per l'analisi completa e le risposte proposte, vai al modulo Recensioni." />
          <div className="divide-y divide-brand-gold/15">
            {place.reviews.map((r) => (
              <div key={r.id} className="py-3 flex gap-3">
                <span className="text-[12px] font-bold text-brand-gold shrink-0 w-10">★ {r.rating}</span>
                <div><div className="text-[12px] text-brand-muted">{r.author} · {r.relative_time || r.date}</div><p className="text-[13.5px] text-brand-ink">{r.text}</p></div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ icon: Icon, text, link }) {
  if (!text) return null;
  return (
    <div className="flex items-center gap-2 text-[13.5px] text-brand-ink">
      <Icon size={14} className="text-brand-gold shrink-0" />
      {link ? <a href={text} target="_blank" rel="noreferrer" className="hover:underline truncate">{text}</a> : <span>{text}</span>}
    </div>
  );
}
